import { ArrowRight, Loader2, Pin, PinOff } from "lucide-react";
import type { Agent } from "@/types/agent/agent";
import type { InboxView, InboxViewRow } from "@/types/inboxView/inboxView";
import { formatCell } from "./formatCell";
import { GenieAttribution } from "./GenieAttribution";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";
import { inboxViewTitle } from "@/lib/inboxView";

interface Props {
  view: InboxView;
  agents: Agent[];
  previewRows?: InboxViewRow[];
  /**
   * Total rows in the view. Use this for the card's count rather than the run's
   * row_count — runs report rows added in the latest refresh (append-only),
   * which is misleading after the initial backfill.
   */
  totalRows?: number;
  onClick: () => void;
  onTogglePin?: () => void;
  pinPending?: boolean;
}

const relativeTime = (iso?: string | null): string => {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const minutes = Math.floor(diffMs / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

export const ViewCard = ({ view, agents, previewRows = [], totalRows, onClick, onTogglePin, pinPending }: Props) => {
  const cols = view.column_schema ?? [];
  const previewCols = cols.slice(0, 5);
  const previewCount = Math.min(3, previewRows.length);
  const lastRun = view.last_run;
  const isActive = lastRun?.status === "designing" || lastRun?.status === "extracting" || lastRun?.status === "pending";
  const totalRowCount = totalRows ?? lastRun?.row_count ?? 0;
  const hasMoreRows = totalRowCount > previewCount;
  const hasMoreCols = cols.length > previewCols.length;

  const handlePinClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTogglePin && !pinPending) onTogglePin();
  };

  return (
    <div
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onClick(); } }}
      className="group w-full text-left bg-white border border-[#E5EAF0] rounded-2xl overflow-hidden hover:border-genie-primary/40 transition-all cursor-pointer"
      style={{ boxShadow: "0 1px 2px rgba(15, 23, 42, .03)" }}
    >
      <div className="px-5 pt-5 pb-3">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-[#4E9CFF]/10 to-[#FF4DB8]/10 flex items-center justify-center shrink-0">
            <GenieIcon
              name="document"
              size={20}
              color="#4E9CFF"
              secondaryColor="#FF4DB8"
              gradientEnabled
              label="View"
            />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-base font-semibold text-[#222B45] leading-snug">
                {inboxViewTitle(view)}
              </span>
              {previewCount > 0 && (
                <span className="shrink-0 inline-flex items-center rounded-full bg-genie-primary/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-genie-primary">
                  Preview
                </span>
              )}
            </div>
            <div className="mt-1.5">
              <GenieAttribution agentIds={view.agent_filter} agents={agents} label="Analyzing" size={20} />
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-[#64748B]">
              <span><strong className="text-[#222B45]">{totalRows ?? lastRun?.row_count ?? 0}</strong> {((totalRows ?? lastRun?.row_count ?? 0) === 1) ? "row" : "rows"}</span>
              <span>·</span>
              <span>{cols.length} columns</span>
              {lastRun?.completed_at && (
                <>
                  <span>·</span>
                  <span>refreshed {relativeTime(lastRun.completed_at)}</span>
                </>
              )}
              {isActive && (
                <>
                  <span>·</span>
                  <span className="inline-flex items-center gap-1 text-genie-primary font-semibold">
                    <Loader2 className="h-3 w-3 animate-spin" /> {lastRun?.status}
                  </span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {onTogglePin && (
              <button
                type="button"
                onClick={handlePinClick}
                disabled={pinPending}
                className={`p-1.5 rounded-lg border border-transparent transition-colors shrink-0 ${
                  view.pinned
                    ? "text-genie-primary hover:bg-genie-primary/10"
                    : "text-[#B6C0CC] hover:text-genie-primary hover:bg-[#FAFCFF]"
                } disabled:opacity-50`}
                title={view.pinned ? "Shown in your inbox. Click to hide it there" : "Show in your inbox"}
                aria-label={view.pinned ? "Hide from inbox" : "Show in inbox"}
              >
                {view.pinned ? <Pin className="h-4 w-4 fill-current" /> : <PinOff className="h-4 w-4" />}
              </button>
            )}
            <span className="hidden sm:inline-flex items-center gap-1 text-sm font-medium text-[#9AA6B2] group-hover:text-genie-primary group-hover:gap-1.5 transition-all">
              Open full view
              <ArrowRight className="h-4 w-4" />
            </span>
            <ArrowRight className="h-5 w-5 text-[#B6C0CC] sm:hidden" />
          </div>
        </div>
      </div>

      {previewCount > 0 && previewCols.length > 0 ? (
        <div className="relative border-t border-[#E5EAF0]">
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-[#FAFCFF]">
                {previewCols.map((c) => (
                  <th key={c.name} className="text-left px-4 py-2 text-[10px] font-semibold uppercase tracking-wider text-[#64748B]">{c.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {previewRows.slice(0, previewCount).map((row, i) => (
                <tr key={i} className="border-t border-[#E5EAF0]">
                  {previewCols.map((c, j) => (
                    <td key={c.name} className="px-4 py-2 text-[#222B45] align-top">
                      <span className={j === 0 ? "font-semibold" : ""}>{formatCell(row.data?.[c.name], c.type)}</span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-6 bg-gradient-to-t from-white to-transparent" />
          <div className="flex items-center justify-between gap-2 px-4 py-2 border-t border-[#E5EAF0] bg-gradient-to-r from-[#4E9CFF]/[0.06] to-[#FF4DB8]/[0.06]">
            <span className="text-[11px] font-medium text-[#64748B]">
              {hasMoreRows || hasMoreCols ? (
                <>
                  Preview only · showing {hasMoreRows ? `${previewCount} of ${totalRowCount} rows` : `all ${totalRowCount} rows`}
                  {hasMoreCols ? ` · ${previewCols.length} of ${cols.length} columns` : ""}
                </>
              ) : (
                "Preview · open the full view to see everything"
              )}
            </span>
            <span className="shrink-0 inline-flex items-center gap-1 text-[11px] font-semibold text-genie-primary group-hover:gap-1.5 transition-all">
              Open full view
              <ArrowRight className="h-3 w-3" />
            </span>
          </div>
        </div>
      ) : (
        <div className="px-5 py-4 text-xs text-[#64748B] border-t border-[#E5EAF0] bg-[#FAFCFF]">
          {isActive ? "Extracting rows..." : "No rows yet. Click to open."}
        </div>
      )}
    </div>
  );
};
